import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { BannedProvisioningIpFilterOption } from '../models/banned-provisioning-ip-filter-option';
import { BannedProvisioningIpRegisterOptions } from '../models/banned-provisioning-ip-register-options';
import { BannedProvisioningIpRegisterListItem } from '../models/banned-provisioning-ip-register-results.model';
import { bannedProvisioningIpRegisterAdapterActions } from './banned-provisioning-ip-register.actions';
export const bannedProvisioningIpRegisterFeatureKey = 'bannedIpProvisioningIpRegister';
export interface BannedProvisioningIpRegisterState
   extends RegisterState<BannedProvisioningIpRegisterListItem, BannedProvisioningIpRegisterOptions, OrderingOption> {
   filters: BannedProvisioningIpFilterOption[];
}

const adapter: RegisterAdapter<
   BannedProvisioningIpRegisterListItem,
   BannedProvisioningIpRegisterOptions,
   OrderingOption
> = createRegisterAdapter<BannedProvisioningIpRegisterListItem, BannedProvisioningIpRegisterOptions, OrderingOption>({
   selectId: (bannedProvisioningIpRegisterItem) => {
      return bannedProvisioningIpRegisterItem.blockedIpAddressId ?? 0;
   },
});

const today = new Date();
const startOfToday = getStartOfDay(today);
const endOfToday = getEndOfDay(today);

const initialState: BannedProvisioningIpRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   orderingOptions: [{ direction: OrderingDirections.Desc, field: 'updateDate' }],
   quickFilters: {
      endDate: endOfToday,
      searchText: null,
      startDate: startOfToday,
      status: null,
      type: null,
   },
};

export const bannedProvisioningIpRegisterReducer = adapter.createReducer(
   bannedProvisioningIpRegisterAdapterActions,
   initialState,
   on(bannedProvisioningIpRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(bannedProvisioningIpRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         bannedIps: action.items,
         totalCount: action.totalCount,
      };
   }),
   on(bannedProvisioningIpRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(bannedProvisioningIpRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   })
);
export const bannedProvisioningIpRegisterSelector = createFeatureSelector<BannedProvisioningIpRegisterState>(
   bannedProvisioningIpRegisterFeatureKey
);
export const selectors = adapter.getSelectors(bannedProvisioningIpRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(bannedProvisioningIpRegisterSelector);

function getStartOfDay(date: Date): Date {
   const startOfDay = new Date(date);
   startOfDay.setHours(0, 0, 0, 0);
   return startOfDay;
}

function getEndOfDay(date: Date): Date {
   const endOfDay = new Date(date);
   endOfDay.setHours(23, 59, 59, 999);
   return endOfDay;
}
